import { useState, useCallback, useMemo } from 'react';

interface UsePerformanceModeOptions {
  defaultThreshold?: number;
  defaultAutoMode?: boolean;
  onModeChange?: (mode: 'normal' | 'high', auto: boolean) => void;
}

export function usePerformanceMode(
  nodeCount: number,
  options: UsePerformanceModeOptions = {}
) {
  const {
    defaultThreshold = 100,
    defaultAutoMode = true,
    onModeChange
  } = options;

  const [performanceMode, setPerformanceMode] = useState<'normal' | 'high'>('normal');
  const [performanceThreshold, setPerformanceThreshold] = useState(defaultThreshold);
  const [autoPerformanceMode, setAutoPerformanceMode] = useState(defaultAutoMode);

  // 检查是否需要启用高性能模式
  const shouldUseHighPerformance = useMemo(() => {
    if (!autoPerformanceMode) return performanceMode === 'high';
    return nodeCount >= performanceThreshold;
  }, [autoPerformanceMode, performanceMode, nodeCount, performanceThreshold]);

  // 切换性能模式
  const togglePerformanceMode = useCallback(() => {
    const newMode = performanceMode === 'normal' ? 'high' : 'normal';
    setPerformanceMode(newMode);
    onModeChange?.(newMode, autoPerformanceMode);
  }, [performanceMode, autoPerformanceMode, onModeChange]);

  // 更新性能模式
  const updatePerformanceMode = useCallback((mode: 'normal' | 'high') => {
    setPerformanceMode(mode);
    onModeChange?.(mode, autoPerformanceMode);
  }, [autoPerformanceMode, onModeChange]);

  // 更新性能阈值
  const updatePerformanceThreshold = useCallback((threshold: number) => {
    setPerformanceThreshold(threshold);
    if (autoPerformanceMode) {
      const newMode = nodeCount >= threshold ? 'high' : 'normal';
      setPerformanceMode(newMode);
      onModeChange?.(newMode, autoPerformanceMode);
    }
  }, [nodeCount, autoPerformanceMode, onModeChange]);

  // 切换自动性能模式
  const toggleAutoPerformanceMode = useCallback(() => {
    const newAutoMode = !autoPerformanceMode;
    setAutoPerformanceMode(newAutoMode);
    
    if (newAutoMode) {
      // 启用自动模式，根据当前节点数量设置性能模式
      const newMode = nodeCount >= performanceThreshold ? 'high' : 'normal';
      setPerformanceMode(newMode);
      onModeChange?.(newMode, newAutoMode);
    } else {
      onModeChange?.(performanceMode, newAutoMode);
    }
  }, [autoPerformanceMode, nodeCount, performanceThreshold, performanceMode, onModeChange]);

  // 更新自动性能模式
  const updateAutoPerformanceMode = useCallback((auto: boolean) => {
    setAutoPerformanceMode(auto);
    
    if (auto) {
      // 启用自动模式，根据当前节点数量设置性能模式
      const newMode = nodeCount >= performanceThreshold ? 'high' : 'normal';
      setPerformanceMode(newMode);
      onModeChange?.(newMode, auto);
    } else {
      onModeChange?.(performanceMode, auto);
    }
  }, [nodeCount, performanceThreshold, performanceMode, onModeChange]);

  // 获取性能状态信息
  const performanceInfo = useMemo(() => ({
    currentMode: shouldUseHighPerformance ? 'high' : 'normal',
    isAutoMode: autoPerformanceMode,
    threshold: performanceThreshold,
    nodeCount,
    statusText: shouldUseHighPerformance ? '🚀 高性能模式' : '✨ 普通模式'
  }), [shouldUseHighPerformance, autoPerformanceMode, performanceThreshold, nodeCount]);

  return {
    performanceMode,
    performanceThreshold,
    autoPerformanceMode,
    shouldUseHighPerformance,
    performanceInfo,
    togglePerformanceMode,
    updatePerformanceMode,
    updatePerformanceThreshold,
    toggleAutoPerformanceMode,
    updateAutoPerformanceMode
  };
}